import { createContext, useContext, useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';

const AuthContext = createContext(null);
const CACHE_KEY = 'di_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/auth/me')
      .then((r) => { setUser(r.data); try { localStorage.setItem(CACHE_KEY, JSON.stringify(r.data)); } catch (e) { /* ignore */ } })
      .catch(() => {
        // Offline field use: keep the operator signed in from the last-known profile so
        // they can keep capturing; the auth cookie persists and sync resumes when back online.
        const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_KEY) : null;
        if (cached && !navigator.onLine) {
          try { setUser(JSON.parse(cached)); return; } catch (e) { /* fall through */ }
        }
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    setError('');
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.otp_required) return { otpRequired: true, email: data.email };
      setUser(data);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) { /* ignore */ }
      return { success: true };
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return { success: false };
    }
  };

  const verifyOtp = async (email, code) => {
    setError('');
    try {
      const { data } = await api.post('/auth/verify-otp', { email, code });
      setUser(data);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) { /* ignore */ }
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (payload) => {
    setError('');
    try {
      const { data } = await api.post('/auth/register', payload);
      setUser(data);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) { /* ignore */ }
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) { /* ignore */ }
    try { localStorage.removeItem(CACHE_KEY); } catch (e) { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, setError, login, verifyOtp, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
